import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { JOB_ROLES } from "@/lib/api";
import { JOB_FIELDS } from "@/lib/schemas";
import api from "@/lib/api";
import DynamicField from "@/components/DynamicField";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

export default function Jobs() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState(null);
  const [data, setData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const pickRole = (r) => {
    // If user is not signed in, send them through staff registration with the role pre-selected
    if (!user) {
      nav(`/register/staff?role=${r.key}`);
      return;
    }
    setRole(r);
    setData({});
  };

  const apply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (user?.role === "staff") {
        await api.post("/staff/apply", { role: role.key, details: data });
      } else {
        await api.post("/jobs/apply", { role: role.key, details: data });
      }
      toast.success("Application received");
      setRole(null);
      setData({});
      if (user?.role === "staff") nav("/staff");
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not submit application";
      toast.error(typeof msg === "string" ? msg : "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="jobs-page">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
        <div className="md:col-span-8">
          <p className="label-eyebrow">Careers</p>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.9] mt-3">
            Join the team<br/>behind the plaza.
          </h1>
        </div>
        <p className="md:col-span-4 text-black/60 self-end">
          Eleven open roles across spa, gym, salon, guesthouse, security, sales and more.
          {user?.role === "staff"
            ? " Pick a role to apply — your account already tracks your applications."
            : " Pick a role to apply — you'll be guided through a quick staff sign-up so you can track status."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        {JOB_ROLES.map((r, idx) => (
          <button
            key={r.key}
            onClick={() => pickRole(r)}
            data-testid={`job-role-${r.key}`}
            className={`md:col-span-4 group text-left border border-black p-6 transition-colors ${role?.key === r.key ? "bg-black text-white" : "bg-white text-black hover:bg-black hover:text-white"}`}
          >
            <p className="font-mono text-xs opacity-60">{String(idx + 1).padStart(2, "0")} / {r.section.toUpperCase()}</p>
            <p className="font-display text-2xl font-black tracking-tight mt-2">{r.name}</p>
            <p className="text-sm mt-3 opacity-70">Open · Apply now</p>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {role && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-16 border-t border-black pt-12"
            data-testid={`job-form-${role.key}`}
          >
            <p className="label-eyebrow">Application</p>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-none mt-3 mb-10">{role.name} — Apply</h2>
            <form onSubmit={apply} className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">
              {(JOB_FIELDS[role.key] || []).map((f) => (
                <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <DynamicField field={f} value={data[f.name]} onChange={setField} context={`job-${role.key}`} />
                </div>
              ))}
              <div className="md:col-span-2 flex gap-4 pt-2">
                <button disabled={submitting} className="btn-primary" data-testid="job-submit">{submitting ? "Submitting…" : "Submit application"}</button>
                <button type="button" onClick={() => setRole(null)} className="btn-ghost">Cancel</button>
              </div>
            </form>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
}
